import { useEffect, useState } from "react";
import { Failure, GhostButton, Loading, Note, PrimaryButton, Screen } from "../components/Entry";
import { csrfToken, go, nodeNamed } from "../lib/flow";
import { createFlow, logoutUrl, submitFlow, whoami, type Session } from "../lib/kratos";
import { href, leave, navigate } from "../lib/router";

const unverified = (session: Session) => session.identity.verifiable_addresses?.find(a => !a.verified)?.value;

/**
 * The relay page for OAuth clients that register /complete-verification-<slug> as a redirect URI. The server holds
 * Hydra's code; once the email is verified, its continue endpoint hands the code to the client.
 */
export function CompleteVerification({ slug }: { slug: string }) {
  const [email, setEmail] = useState<string>();
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string>();
  const proceed = () => leave(`/complete-verification/${slug}/continue`);

  // The link usually opens in another tab or on another device; this page follows along.
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const session = await whoami().catch(() => undefined);
      if (!alive || session === undefined) return;
      if (!session) return navigate(href("/login", { return_to: window.location.href }), { replace: true });
      const address = unverified(session);
      if (!address) return proceed();
      setEmail(address);
    };
    void check();
    const timer = window.setInterval(check, 3000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const resend = async () => {
    setPending(true);
    setFailure(undefined);
    try {
      const started = await createFlow("verification", { return_to: window.location.href });
      if (started.kind !== "flow") throw new Error();
      const flow = started.flow;
      const byCode = Boolean(nodeNamed(flow, "code"));
      const outcome = await submitFlow(flow, { csrf_token: csrfToken(flow), method: byCode ? "code" : "link", email });
      if (outcome.kind !== "flow" || outcome.flow.state !== "sent_email") throw new Error();
      // A code has to be typed in; the verification page does that and comes back here.
      if (byCode) return navigate(href("/verification", { flow: flow.id }));
      setSent(true);
      window.setTimeout(() => setSent(false), 30_000);
    } catch {
      setFailure("We couldn’t send the email. Try again in a moment.");
    } finally {
      setPending(false);
    }
  };
  const signOut = async () => go((await logoutUrl(window.location.origin + "/login")) ?? "/login");

  if (!email) return <Loading />;
  return (
    <Screen title="Confirm your email">
      <form
        noValidate
        className="entry-form"
        onSubmit={event => {
          event.preventDefault();
          void resend();
        }}
      >
        <Note>We sent a verification link to {email}. Open it to continue; this page moves on by itself.</Note>
        {sent && <Note>We sent a new link.</Note>}
        <Failure text={failure} />
        <PrimaryButton pending={pending} disabled={sent}>
          Send another link
        </PrimaryButton>
        <GhostButton onClick={() => void signOut()}>Use another account</GhostButton>
      </form>
    </Screen>
  );
}
